/**
 * Admin top bar — TypeScript types.
 *
 * @see ../../.claude/CLAUDE.md sekcje "Komponenty admin (in-place editing)"
 *      + "AdminContext (state globalny)"
 */

import type { AreaSymbol, LayoutSymbol } from "@/lib/layouts/types";
import type { UnpublishedWindow, WindowsLayoutState } from "@/lib/windows/types";
import type { AdminMessagesShape } from "@/lib/i18n/admin";
import type { BreadcrumbItem } from "@/lib/cms/breadcrumb";

/** Typ aktualnej strony — homepage vs catalog subpage. Decyduje który zestaw layoutów dropdown w panelu pokazuje. */
export type PageType = "homepage" | "catalog";

/**
 * Zakres zapisu okien/layoutu w panelu admina (article view only).
 *
 * Mapowanie do `imp_cms_catalog_window.type`:
 *   - `this_url`     -> type=3 + request_uri = bieżący URL (per-article override, NAJWYŻSZY priorytet w cascade)
 *   - `all_articles` -> type=2 (wszystkie artykuły w katalogu, applied jako fallback gdy type=3 brak)
 *   - `catalog`      -> type=1 (cały katalog — index + wszystkie artykuły jako default; najniższy priorytet w cascade)
 *
 * Backend cascade dedupe (`cascadeDedupePerArea` w windows_manager.php) bierze
 * najwyższy `type` per (area, sort) — więc admin może override'ować pojedyncze
 * pozycje per-article zachowując resztę z catalog.
 *
 * Catalog index view (currentArticleId === null) ZAWSZE używa `catalog` —
 * radio nie jest renderowane, AdminContext zwraca `catalog` statycznie.
 */
export type SaveScope = "this_url" | "all_articles" | "catalog";

/** Aktywny tab w top barze — null = brak (admin przegląda jak normalny user). */
export type ActiveTab = "catalog" | "layoutAndWindows" | null;

/** Mock user (Faza G MVP) — w przyszłości pochodzi z `/IRESTAPI/v1/IIAPI_CONTROLLER/session`. */
export type AdminUser = {
  id: number;
  login: string;
  name: string;
  surname?: string;
  email?: string;
};

/**
 * Snapshot konfiguracji `template_default` dla read-only preview mode.
 * Hydratowane w PublicLayout z `IAPI_templateDefaultPreview` (server-side
 * fetch), trzymane w AdminContext. Toggle `previewMode` decyduje czy context
 * wystawia te dane zamiast committed (page render switches to default).
 *
 * Shape jest takim samym jak `currentLayout` + `windowsState` + `pageWindows`
 * w głównym contextcie — frontend może je podmienić 1:1.
 */
export type PreviewData = {
  /** Symbol layoutu z template_default (np. `LCatalogTwoColsLeft`).
      NULL gdy backend nie zwrócił prawidłowego symbolu. */
  layout: LayoutSymbol | null;
  /** Okna pogrupowane per area (analog do committed `windowsState`). */
  windowsState: WindowsLayoutState;
  /** Metadata okien dla render'u (analog do committed `pageWindows`). */
  pageWindows: UnpublishedWindow[];
};

/** Notyfikacja — Faza G MVP placeholder, real data z `/notifications/list` w Fazie F.5+. */
export type AdminNotification = {
  id: number;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
};

export type AdminContextValue = {
  user: AdminUser | null;
  scopes: string[];
  hasScope: (scope: string) => boolean;

  /** Konfiguracja CMS (logo_url, logo_alt, theme flags) — przekazana z PublicLayout
      żeby admin UI (np. mini-mapa w LayoutAndWindowsPanel) mogło renderować
      ten sam logo source co visitor view. NULL gdy fetch config się nie powiódł. */
  cmsConfig: import("@/lib/api/cms-config").CmsConfig | null;

  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;

  /** Slug aktualnej strony ("/" dla homepage, "/o-nas" dla catalog). Z prop'a z page'a. */
  currentSlug: string;
  /** Typ aktualnej strony — decyduje pul layoutów (PAGE_LAYOUTS vs CATALOG_LAYOUTS) w panelu. */
  currentPageType: PageType;
  /** Aktualne locale z URL prefix ('pl', 'en', 'de') — przekazywane jako
      `X-Imperial-Lang` w Server Actions. */
  locale: string;
  /** Backend `imp_cms_catalog.id` dla `currentPageType="catalog"`, NULL dla
      homepage. Używane do budowania IADMIN deep-link'ów per-katalog
      (Konfiguruj, Zarządzaj treścią, Usuń, Dodaj podstronę). */
  currentIdCatalog: number | null;
  /** ID artykułu (`parseSlug.articles[0]?.id`) — non-null gdy bieżący URL ma
      article extras. Decyduje czy panel pokaże radio `saveScope`. NULL = catalog
      index / homepage -> save zawsze leci jako 'catalog'. */
  currentArticleId: number | null;
  /** Canonical request URI bez locale prefix (np. `/blog/lorem-42`) — zapisywany
      jako `request_uri` przy `saveScope='this_url'`. NULL gdy nie jesteśmy
      na artykule. */
  currentRequestUri: string | null;
  /** Aktualny zakres zapisu okien/layoutu — domyślnie `'this_url'` na artykule,
      `'catalog'` na index. Zmieniany przez radio w `LayoutAndWindowsPanel`.
      `persistWindowsState` + `setLayoutForCurrentPage` używają go do wyliczenia
      `(type, requestUri)` przy save'ie. */
  saveScope: SaveScope;
  /** Setter — zmiana radio w panelu. Tylko dla article view; na catalog index
      wywołanie no-op (saveScope wymuszony na 'catalog'). */
  setSaveScope: (scope: SaveScope) => void;

  /** Layout AKTUALNEJ strony — derived z currentSlug + per-page mapy. */
  currentLayout: LayoutSymbol;
  /** Setter — operuje na current slug. Server Action zapisuje cookie + revalidate. */
  setLayoutForCurrentPage: (symbol: LayoutSymbol) => void;

  /** Windows state AKTUALNEJ strony — mapping area -> uporządkowana lista windowId. */
  windowsState: WindowsLayoutState;
  /** Dodaje windowId do danego area. Bez `atIndex` -> append na końcu (drop na area placeholder).
      Z `atIndex` -> insert przed slotem o tym indexie (drop na konkretne miejsce w liście). */
  addWindowToArea: (windowId: number, area: AreaSymbol, atIndex?: number) => void;
  /** Reorder windowId w obrębie area na nowy index (insert before, push others). */
  moveWindowInArea: (windowId: number, area: AreaSymbol, toIndex: number) => void;
  /** Move windowId między area'ami na konkretny index (lub push na koniec). */
  moveWindowBetweenAreas: (
    windowId: number,
    fromArea: AreaSymbol,
    toArea: AreaSymbol,
    toIndex?: number,
  ) => void;
  /** Usuń okno z area (powrót do unpublished pool). */
  removeWindowFromArea: (windowId: number, area: AreaSymbol) => void;
  /** Bulk commit całego windows state — diff'uje z aktualnym stanem, persistuje
      WSZYSTKIE zmienione obszary jednym przebiegiem. Używane przez draft mode
      w `LayoutAndWindowsPanel` (admin akumuluje zmiany lokalnie + klika
      "Zatwierdź zmiany"). */
  commitWindowsState: (next: WindowsLayoutState) => Promise<void>;

  /** ID aktualnie przeciąganego okna (panel lub slot). null = brak draga w toku.
      Używane przez WindowArea do podświetlania allowedAreas vs disabling not-allowed. */
  draggedWindowId: number | null;
  setDraggedWindowId: (id: number | null) => void;

  /** ID okna zaznaczonego (rozszerzonego) w panelu "Nieopublikowane okna". null
      = brak selekcji. Stan lifted do contextu żeby globalny dev banner mógł
      reagować na selekcję (np. ostrzec o braku skonfigurowanych allowedAreas). */
  selectedWindowId: number | null;
  setSelectedWindowId: (id: number | null | ((prev: number | null) => number | null)) => void;

  /** Widoczność dev banera (`<AlignmentChecker>`) — toggle przyciskiem w
      AdminTopBar (ikona widoczna tylko w `NODE_ENV=development`). Persistowany
      w localStorage żeby przeładowanie strony zachowywało ustawienie. */
  devBannerHidden: boolean;
  setDevBannerHidden: (hidden: boolean) => void;

  /** **DEV-only:** czy `WDropDownMenu` ma wstrzykiwać przykładowe drzewo
      (5 poziomów) do testowania layoutu mega-menu. Default `true` w dev'ie,
      toggle przyciskiem w AdminTopBar (widoczny TYLKO w `NODE_ENV=development`).
      Persistowany w localStorage. */
  devMockDropDownTree: boolean;
  setDevMockDropDownTree: (enabled: boolean) => void;

  /** Liczba aktywnych async requestów w admin panelu (Server Actions, fetch'e).
      Wykorzystywana przez `<AdminPendingBar>` (sticky preloader top-of-page)
      do pokazania że "coś w tle się dzieje". 0 = idle. */
  pendingRequests: number;
  /**
   * Wrapper na asynchroniczną operację — inkrementuje `pendingRequests` przed,
   * dekrementuje po zakończeniu (sukces lub błąd). Standardowy idiom dla
   * wszystkich akcji admin panelu które wywołują Server Action / fetch.
   *
   * @example
   * await withPendingRequest(async () => saveAreaWindowsAction({...}));
   */
  withPendingRequest: <T>(fn: () => Promise<T>) => Promise<T>;

  /** Meta okien NA AKTUALNEJ STRONIE (lookup dla visitor render).
      Source: `publishedWindowsData` z `GET /cms/windows` (cascade resolution).
      Build w PublicLayout przez `adaptCmsWindow` — ZAWSZE dostępne (visitor + admin).
      Visitor view (WindowAreaView/WindowContainer/WindowRenderer) używa TYLKO
      tego pola — render strony jest identyczny dla każdej roli. */
  pageWindows: UnpublishedWindow[];

  /** Pula DRAFTÓW — okna w `imp_cms_window` które NIE są przypisane do żadnej
      strony (`imp_cms_catalog_window` puste).
      Source: REST API `GET /cms/windows/unpublished` (Bearer, admin only).
      Tylko admin go widzi — drag source w `LayoutAndWindowsPanel`.
      Visitor / nie-admin: pusty array. */
  unpublishedWindows: UnpublishedWindow[];

  /** Lista layoutów dostępnych dla bieżącej domeny (z `imp_cms_domain_template_layout`).
      Source: REST API `GET /cms/layouts` (fetched server-side w PublicLayout).
      Pusta lista = admin nie przypisał layoutów -> panel pokazuje pustą select listę,
      AlignmentChecker raportuje `no_layouts_assigned` issue z linkiem do admin URL. */
  availableLayouts: import("@/lib/api/cms-layouts").CmsLayout[];

  /** Mapa `id_window` -> **Promise** content'u okna (NIE awaitowany w PublicLayout).
      WindowRenderer używa React's `use(promise)` hook -> React suspend'uje per
      okno do czasu resolve. Pozwala stream'ować chunky HTML do przeglądarki:
      chrome (header/footer/layout) renderuje się natychmiast, content okien
      fill'uje się niezależnie w miarę resolve'owania backend'owych fetchy.
      Promise może resolve do `null` (network/500 error — patrz `fetchWindowContent`). */
  windowContents: Map<
    number,
    Promise<import("@/lib/api/cms-window-content").WindowContentResponse<unknown> | null>
  >;

  /** Lista aktywnych języków bieżącej domeny — dla `AdminLocaleSwitch` w top
      barze. Każdy język to osobna "strona" CMS (różny układ + okna +
      konfiguracja per `id_domain_language`). Pusta tablica = backend nie
      zwrócił danych (fallback do tylko aktualnego locale z URL).
      Source: `GET /cms/domain-languages` (fetched server-side w PublicLayout). */
  domainLanguages: import("@/lib/api/domain-languages").DomainLanguage[];

  /** Skąd przyszła obecna konfiguracja layoutu (z meta API):
      - `catalog` — per-page (admin pracuje z własną config tej podstrony)
      - `template_default` — global default (banner: zmiany utworzą per-page override)
      - `fallback` — frontend hardcoded (banner: brak default'a, zachęta do "Zapisz domyślny")
      - `invalid_for_template` — backend cascade znalazł layout ale nie jest przypisany
        do aktywnego szablonu domeny; strona renderuje się jako maintenance / admin
        banner z linkiem do IADMIN > Domeny > Szablony. */
  layoutSource: "catalog" | "template_default" | "fallback" | "invalid_for_template";
  /** Skąd przyszły okna (zazwyczaj zgodne z `layoutSource`). */
  windowsSource: "catalog" | "template_default" | "fallback" | "invalid_for_template";
  /** Czy w bazie istnieje wiersz `is_global_default=1` dla bieżącej domeny.
      Wpływa na label button'a "Zapisz domyślny" vs "Nadpisz domyślny" + treść
      confirmDialog'a. */
  defaultTemplateExists: boolean;
  /** Symbol aktywnego szablonu domeny (z `cfg()->template` w backendzie).
      NULL = domena nie ma ustawionego szablonu. Wykorzystywane w komunikatach
      dla admina (np. "Aktywny szablon 'X' nie ma layoutów dla podstron…"). */
  activeTemplate: string | null;
  /** Liczba layoutów przypisanych do aktywnego szablonu z `supports_homepage=1`. */
  availableLayoutsCountHomepage: number;
  /** Liczba layoutów przypisanych do aktywnego szablonu z `supports_subpage=1`. */
  availableLayoutsCountSubpage: number;
  /** Symbol bieżącej domeny (np. "dev.v7.imperialcloud.io"). Z config.json /
      PublicLayoutProps.domain. Pomaga adminowi orientować się którą domeną
      zarządza w dev bannerze. */
  domain: string;
  /** ID bieżącej domeny w bazie Imperial (`imp_domain.id`). NULL gdy backend
      nie zwrócił meta (np. brak sesji). Dev banner używa do deep-link'ów
      do IADMIN > Domeny > [id] > {action}. */
  idDomain: number | null;
  /** ID grupy domen. NULL przy braku context'u. */
  idDomainGroup: number | null;
  /** ID wiersza `imp_cms_domain_template` dla AKTYWNEGO szablonu w bieżącej
      domenie — używane jako `id_element` w deep-link'u do edycji layoutów per
      (domena, szablon). NULL gdy szablon nie jest enabled dla domeny. */
  idActiveDomainTemplate: number | null;

  /** Lista `area_key` które są aktualnie disabled w bieżącym scope (cascade-resolved
      z `imp_cms_catalog_area_state` per area_key niezależnie). Hydratowane z meta
      backendu (`disabled_areas` zwracane przez `IAPI_currentLayout` / `IAPI_windows`)
      + aktualizowane optymistycznie przy `setAreaEnabled`. Konsumenci:
        - `<WindowArea>` — early return żeby NIE renderować strefy (collapse layoutu)
        - `LayoutAndWindowsPanel` — toggle obok nagłówka strefy + wizualne wyszarzenie
          panelu strefy gdy wyłączona. */
  disabledAreas: AreaSymbol[];

  /** Commit batched diff'a stanu disabled obszarów względem `disabledAreas`
      (committed). Wywoływane przez `LayoutMap.handleCommit` razem z
      `commitWindowsState`. Per zmieniony obszar uruchamia `saveAreaStateAction`,
      aktualizuje optymistycznie context.disabledAreas, wyświetla pojedynczy
      toast po zakończeniu (sukces/błąd). Scope rozwiązywany przez
      `resolveSaveTarget()`. Catalog area (`is_catalog_area=1`) odrzucane na
      backendzie — frontend powinien nie pokazywać toggle dla takich obszarów. */
  commitAreaStateChanges: (draftDisabledAreas: AreaSymbol[]) => Promise<void>;

  /** Granular CMS permissions z `session.cmsPermissions` (z backend
      `ssoCurrentSession`). NULL gdy brak admin session (visitor lub
      logged-in non-admin — frontend traktuje obu jako visitor). */
  cmsPermissions: import("@/lib/api/session").CmsPermissions | null;
  /** Shortcut — czy user ma uprawnienia do edycji (`modify`). Używane w
      UI gating: drag&drop, save layout, scope radio, area toggle etc.
      = `cmsPermissions?.modify === true`. */
  canModify: boolean;
  /** Shortcut — czy user ma uprawnienia do usuwania okien (`delete`).
      Używane do disable'u "Usuń okno" + DELETE action.
      = `cmsPermissions?.delete === true`. */
  canDelete: boolean;

  /** Per-katalogowe uprawnienia bieżącego admina (z backend
      `imp_user_group_privilege` × `currentIdCatalog`). Dolewane przez
      `IAPI_pageBootstrap` TYLKO gdy session aktywna + user adminem +
      katalog istnieje. NULL = visitor / non-admin / homepage. UI gate'uje
      przyciski katalogu (Konfiguruj / Usuń / Dodaj podstronę / Zarządzaj
      treścią) per uprawnienie. */
  catalogPermissions: import("@/lib/api/session").CatalogAdminPermissions | null;
  /** Shortcut — czy admin może modyfikować bieżący katalog (zmiana właściwości,
      layout, dodanie podstrony pod nim). Łączy windows_manager `modify` z
      per-catalog `modify`. Gdy `catalogPermissions === null` -> fallback na
      sam `canModify` (bo nie ma katalogu = nie ma per-cat checku, np. homepage).
      = `canModify && (catalogPermissions === null || catalogPermissions.modify)`. */
  canModifyCatalog: boolean;
  /** Shortcut — czy admin może usunąć bieżący katalog. Łączy windows_manager
      `delete` z per-catalog `delete`. Dodatkowo backend `catalogs.php` blokuje
      redaktorów (`isRedactor && !isOwner`) nawet z `delete=1` — frontend
      respektuje to przez `is_owner` flag (redaktor bez ownership nie może
      usuwać). */
  canDeleteCatalog: boolean;

  /** Preview mode — gdy `true`, kontekst wystawia dane z `previewData`
      (template_default) zamiast committed. Wszystkie akcje edycji disabled
      (commit, drag&drop, save buttons, scope radio). Toggle przez
      `togglePreviewMode()`. */
  previewMode: boolean;
  /** Toggle preview mode on/off. No-op gdy `previewData === null` (brak default
      template w domenie — toggle nie pokaże się też w UI). */
  togglePreviewMode: () => void;
  /** Dane template_default (layout + okna) — hydratowane server-side w
      PublicLayout z `IAPI_templateDefaultPreview`. NULL gdy domena nie ma
      template_default lub fetch failed. */
  previewData: PreviewData | null;


  /** Trasa breadcrumb dla bieżącej strony (lista entries: home + przodki +
      bieżący katalog + ewentualnie artykuł). Server-side built w `PublicLayout`
      z danych już dostępnych w bootstrap response (`catalog_content.catalog_path`
      LUB `response_catalog_data.path` override) — BEZ dodatkowego endpointa.
      Konsumowane przez okno `w_path` (komponent WPath); inne komponenty też
      mogą czytać (np. SEO JSON-LD, sticky header z breadcrumb'em).
      Dla homepage = `[home]` (jedna pozycja). */
  pageBreadcrumb: BreadcrumbItem[];

  notifications: {
    unreadCount: number;
    recent: AdminNotification[];
    markAsRead: (id: number) => void;
  };

  /** URL admin panelu Imperial (do button "Panel admina ↗") — z `process.env.IMPERIAL_API_BASE_*`. */
  adminPanelUrl: string;

  /** `true` gdy wydruk podstrony do PDF jest dostępny (rozszerzenie
      `cms_pdf_print` + silnik Chromium na serwerze) — z
      `cmsConfig` meta (`pdf_export_available`). Gate'uje przycisk
      "Drukuj do PDF" w `CatalogAdminToolbar`. */
  pdfExportAvailable: boolean;

  /** Aktualny język UI panelu admina (`"pl"`, `"en"` itd.). Source-of-truth:
      backend `$_SESSION['sa_lang']` (shared z legacy IADMIN), expose przez
      `IAPI_ssoCurrentSession`. **Read-only** — zmiana języka WYŁĄCZNIE w
      classic IADMIN (przełącznik górnego paska). React-CMS odczytuje wartość
      przy każdym request'cie i automatycznie pokazuje nowe tłumaczenia. */
  adminLocale: string;
  /** Tłumaczenia panelu admina dla bieżącego `adminLocale`. Zawsze dostępne
      (fallback do default locale w `getAdminMessages`). Komponenty admin
      czytają stringi przez `useAdmin().t.{namespace}.{key}`. */
  t: AdminMessagesShape;
};

/** Cookies names — single source of truth dla persistence. */
export const COOKIE_ACTIVE_TAB = "cms_active_tab";
/** Cookie nazwa preferencji "dev banner schowany" — wartość `"1"` = hidden,
    `"0"` lub brak = visible. Cookie zamiast localStorage żeby server-side render
    znał stan przed hydration -> brak flash'a po reload (banner nie pojawia się
    na chwilę zanim React odczyta preferencję). */
export const COOKIE_DEV_BANNER_HIDDEN = "cms_dev_banner_hidden";

/** Type guard — czy string jest valid ActiveTab (z null'em). */
export function isActiveTab(value: unknown): value is ActiveTab {
  return value === "catalog" || value === "layoutAndWindows" || value === null;
}
